import { createFileRoute } from "@tanstack/react-router";
import { ActivityRouteView } from "@/components/activity/activity-route-view";
import { EventsFeed } from "@/components/entity-tabs/events-tab/events-feed";
import { PageColumn, PageShell } from "@/components/ui/page-shell";
import { useWorkspaceRoot } from "@/lib/api";
import { parseEventFiltersFromUrl } from "@/lib/event-filters";
import { pruneSearch } from "@/lib/router-search";

/**
 * Workspace owners render the org-wide activity stream. The reserved
 * `me` owner renders only the authenticated user's exact MCP activity or audit
 * detail, without resolving a workspace.
 */
export const Route = createFileRoute("/$owner/events/")({
	loader: ({ context: { queryClient }, params }) => {
		if (params.owner === "me") return;
		void queryClient.prefetchQuery(
			useWorkspaceRoot.queryOptions(params.owner, { slug: params.owner }),
		);
	},
	component: EventsPage,
	validateSearch: (search: Record<string, unknown>) =>
		pruneSearch(search) as Record<string, string | undefined>,
});

function EventsPage() {
	const { owner } = Route.useParams();
	const search = Route.useSearch();

	if (owner === "me") {
		const parsed = parseEventFiltersFromUrl(search);
		const filters = {
			sortBy: "date" as const,
			sortOrder: "desc" as const,
			...(parsed.contentIds?.length
				? { contentIds: parsed.contentIds }
				: { clientIds: parsed.clientIds, mcpActivityId: parsed.mcpActivityId }),
		};
		return (
			<PageShell className="gap-6 pt-2.5 pb-8">
				<PageColumn className="flex flex-col gap-6">
					<h1 className="text-lg font-semibold">Your MCP activity</h1>
					<EventsFeed organizationId={null} ownerSlug="me" filters={filters} />
				</PageColumn>
			</PageShell>
		);
	}

	return <ActivityRouteView owner={owner} search={search} />;
}
